import { useEffect, useState } from 'react';
import { useSplash } from '../../ui/splash/SplashContext';
import { isReactivationInProgress } from '../../utils/billing/reactivationInProgress';
import { SPLASH_PHASE, getSplashPhase, getSplashLeaveSeq } from '../../ui/splash/splashHandoff';

/**
 * First-load splash hold (RD-VIS / boot-flicker).
 *
 * On the VERY FIRST role-home data load after a full page load, hold the
 * singleton splash over the loading state so boot reads as splash → real data
 * (no skeleton / empty-state flash). Subsequent loads (tab switches, refetches)
 * fall through to the normal skeleton.
 *
 * Two wire-in shapes:
 *   • Skeleton-driven homes (admin/operations/billing/worker/supervisor/platform) call this from
 *     {@link DashboardLoadSkeleton}, which mounts only while `loading` — so the
 *     hold is bounded by the skeleton's own mount/unmount (`active` stays true;
 *     data-ready unmounts the skeleton → splash auto-clears). No page edit.
 *   • Platform dashboard: {@link PlatformDashboardTab} mounts the same skeleton
 *     while `dashboardStats === null`; {@link usePlatformDashboardData} also
 *     holds splash until the tenant list resolves (any tab on first boot).
 *
 * Safety net: if data is slow, release after `maxDwellMs` so the skeleton /
 * dashboard (progress feedback) is revealed rather than a frozen wordmark.
 * 2026-09-25 splash audit: 700 → 3000ms. At 700ms most real boots fell
 * through to the skeleton, which then cross-faded AGAIN into content — two
 * transitions read as flicker. Now a normal boot is ONE handoff (splash →
 * real data); only a genuinely slow network shows the skeleton.
 *
 * `firstLoadConsumed` is module-scoped → resets on a real reload (we DO want
 * the hold on every fresh boot) but not on in-session SPA navigation.
 */

let firstLoadConsumed = false;
// ONE 3s budget per boot shared by the chunk-fallback hold and the home's own
// data hold (code review, KAN-562 follow-up): without it the unclaimed chunk
// hold plus a fresh claimed hold could keep a no-welcome user on the splash
// for chunk time + 3s. Set when the first hold of this boot starts.
let firstHoldStartedAt = null;

// The splash is (still) on its first cover: covering now, or never left yet
// (a same-commit mount can run before the overlay publishes COVERED).
function splashStillOnFirstCover() {
  const phase = getSplashPhase();
  return phase === SPLASH_PHASE.COVERED || (phase === SPLASH_PHASE.CLEAR && getSplashLeaveSeq() === 0);
}
// 'operations' (KAN-562 follow-up): AdminHome renders the operations
// skeleton variant, which was missing here — the one tenant role whose
// sign-in dropped the splash onto a skeleton before its home (and its role
// welcome) had mounted, so the install sheet could take the screen first.
const HOLD_VARIANTS = new Set(['admin', 'operations', 'billing', 'worker', 'supervisor', 'platform']);

export const FIRST_LOAD_MAX_DWELL_MS = 3000;

/**
 * A new sign-in is a new first load (2026-09-26): without this, signing out and
 * back in without a reload skipped the hold and flashed the skeleton after the
 * splash. Called by signInCover.raiseSignInCover.
 */
export function resetFirstLoadSplash() { firstLoadConsumed = false; firstHoldStartedAt = null; }

/**
 * `claim: false` — hold over this loading state WITHOUT spending the one-shot
 * first-load claim (KAN-562 follow-up): AppShell's chunk-load fallback. It is
 * still the first boot, but the home's own data load must keep its full hold
 * (and the welcome gate its splash) — the chunk wait used to burn the whole
 * 3s dwell and the splash left before the home mounted.
 */
export default function useFirstLoadSplash(variant, active = true, { maxDwellMs = FIRST_LOAD_MAX_DWELL_MS, claim = true } = {}) {
  // An UNCLAIMED hold (chunk fallback) only ever extends a splash that is
  // still covering the first boot — never raises it later: a lazy tab chunk
  // loading mid-session must not bring the wordmark back, even when the home
  // never rendered its own skeleton to spend the claim.
  const [holding, setHolding] = useState(
    // KAN-877: a suspended-screen reactivation (card -> celebration) owns the screen; the first-load hold must not raise the splash under it.
    () => HOLD_VARIANTS.has(variant) && !firstLoadConsumed && active && !isReactivationInProgress()
      && (claim || splashStillOnFirstCover()),
  );

  // First load finished (active → false): release so real data is revealed.
  useEffect(() => {
    if (!active) setHolding(false);
  }, [active]);

  // One-shot claim + slow-load fallthrough.
  useEffect(() => {
    if (!holding) return undefined;
    if (claim) firstLoadConsumed = true; // only the first boot holds
    const now = Date.now();
    if (firstHoldStartedAt == null) firstHoldStartedAt = now;
    const remaining = Math.max(0, maxDwellMs - (now - firstHoldStartedAt));
    const timer = setTimeout(() => {
      // An unclaimed hold that ran out has shown the skeleton: the home that
      // mounts next must not raise the splash again over it (no blip).
      if (!claim) firstLoadConsumed = true;
      setHolding(false);
    }, remaining);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While holding, the continuous splash covers the loading state beneath.
  // `optional` so a skeleton rendered outside SplashProvider (unit tests) no-ops.
  useSplash(holding, { immediate: true, optional: true });
}
